import { EventEmitter } from "events";
import assign from "object-assign";
import { supportedLanguages as langMap } from "../../../assets/i18h/appTranslation";
import ApiService from "../../../services/ApiService";
import phoneService from "../../../services/phoneService";
import {
  getProfilePhoneError,
  isProfilePhoneEmpty,
  isOptionalDobValid,
  isPasswordPolicyMet,
  isValidProfession,
  trimRequired,
} from "./registrationWizardValidation";
import {
  saveAboutYouStep,
  saveClinicalStep,
  saveClinicSettingsStep,
  savePhotoPhoneStep,
  saveSecurityStep,
  saveWelcomeStep,
  isV2SaveSuccess,
} from "./registrationWizardApi";
import { normalizeClinicianRole, REGISTRATION_SCENARIOS } from "./registrationWizardConstants";
import {
  applyApiAccountToStepData,
  hasExplicitLanguage,
  resolveLanguageFromGeo,
} from "./registrationWizardPrefill";
import {
  buildStepPipeline,
  hasNameOnFile,
  hasProfilePhotoOnFile,
  parseProfessionsFromApi,
  WIZARD_STEP_IDS,
  needsPasswordOnSecurityStep,
  needsTermsOnSecurityStep,
} from "./registrationWizardSteps";

const localStorageKeyForScenario = (scenario) =>
  `wc_clinician_registration_wizard_${scenario || REGISTRATION_SCENARIOS.SUB_ACCOUNT}`;
const CHANGE_EVENT = "change";

const createEmptyStepData = () => ({
  firstName: { value: "", isValid: true },
  lastName: { value: "", isValid: true },
  password: { value: "", isValid: true },
  acceptTerms: { value: false, isValid: true },
  profession: { value: "", isValid: true },
  experienceSince: { value: "", isValid: true },
  languageForPatients: { value: langMap.en, isValid: true },
  languageForPatientsTouched: { value: false },
  photo: { value: null, isValid: true },
  phone: { value: phoneService.emptyPhone(), isValid: true },
  phoneEdited: { value: false },
  gender: { value: "", isValid: true },
  birthMonth: { value: "", isValid: true },
  birthDay: { value: "", isValid: true },
  birthYear: { value: "", isValid: true },
  clinicianLanguage: { value: langMap.en, isValid: true },
});

let _stepIndex = 0;
let _stepData = createEmptyStepData();
let _account = {};
let _stepPipeline = buildStepPipeline({});

const RegistrationWizardState = assign({}, EventEmitter.prototype, {
  addChangeListener(callback) {
    this.on(CHANGE_EVENT, callback);
  },
  removeChangeListener(callback) {
    this.removeListener(CHANGE_EVENT, callback);
  },
  emitChange() {
    this.emit(CHANGE_EVENT);
  },

  init(account, options = {}) {
    _account = account || {};
    _stepIndex = 0;
    const skipStoredProgress = options.fresh || options.fromApi;

    if (!skipStoredProgress) {
      try {
        const storageKey = localStorageKeyForScenario(_account.registrationScenario);
        const saved = JSON.parse(localStorage.getItem(storageKey) || "");
        if (saved?.expiry > Date.now() && saved.data) {
          _stepData = { ...createEmptyStepData(), ...saved.data };
          if (_stepData.phone?.value) {
            _stepData.phone.value = phoneService.normalizeSubAccountPhone(_stepData.phone.value);
          }
          _stepIndex = saved.stepIndex || 0;
          if (Array.isArray(saved.pipeline) && saved.pipeline.length) {
            _stepPipeline = saved.pipeline;
          }
          this.emitChange();
          return;
        }
      } catch (e) {
        // ignore
      }
    }

    _stepData = createEmptyStepData();
    applyApiAccountToStepData(_stepData, account);
    if (
      hasNameOnFile(_account) ||
      hasNameOnFile({
        firstName: _stepData.firstName.value,
        lastName: _stepData.lastName.value,
      })
    ) {
      _account.hasNameOnFile = true;
    }
    if (hasProfilePhotoOnFile(_account, _stepData.photo.value)) {
      _account.hasProfilePhotoOnFile = true;
    }
    _stepPipeline = buildStepPipeline(_account, _stepData.photo.value);
    this.emitChange();
  },

  /**
   * Prefetch ipapi on wizard step 2 so Default Patients Language is ready before the clinic step.
   * Stored account language is always `en` and cannot signal a real choice; only a manual pick
   * inside the wizard wins. English when geo fails or the country is unmapped.
   */
  applyGeoLanguageDefaults() {
    const needsPatientLang = !_stepData.languageForPatientsTouched.value;
    const needsClinicianLang = !hasExplicitLanguage(_account.clinicianLanguage);

    return ApiService.getGeoLocation({ retryFailed: true })
      .then((info) => {
        // resolveLanguageFromGeo yields English for a failed lookup or an unmapped country.
        const lang = resolveLanguageFromGeo(info || {});
        let changed = false;
        if (needsPatientLang && !_stepData.languageForPatientsTouched.value) {
          _stepData.languageForPatients.value = lang;
          changed = true;
        }
        if (needsClinicianLang) {
          _stepData.clinicianLanguage.value = lang;
          changed = true;
        }
        if (changed) {
          this.persist();
          this.emitChange();
        }
      })
      .catch(() => {
        // Keep createEmptyStepData / prefill defaults when geo is unavailable.
      });
  },

  async finalizeStepPipeline({ demo = false } = {}) {
    if (isValidProfession(_stepData.profession.value)) {
      _account.hasProfessionOnFile = true;
      _account.profession = _stepData.profession.value;
    }

    if (!demo && !_account.hasProfessionOnFile) {
      try {
        const professionsData = await ApiService.getTherapistProfessions();
        const parsed = parseProfessionsFromApi(professionsData);
        if (parsed) {
          _stepData.profession.value = parsed.profession;
          if (parsed.experienceSince) {
            _stepData.experienceSince.value = parsed.experienceSince;
          }
          _account.hasProfessionOnFile = true;
          _account.profession = parsed.profession;
        }
      } catch (e) {
        ApiService.logDispatcherError("v2 my/professions unavailable", e);
      }
    }

    if (
      hasNameOnFile(_account) ||
      hasNameOnFile({
        firstName: _stepData.firstName.value,
        lastName: _stepData.lastName.value,
      })
    ) {
      _account.hasNameOnFile = true;
    }
    if (hasProfilePhotoOnFile(_account, _stepData.photo.value)) {
      _account.hasProfilePhotoOnFile = true;
    }

    _stepPipeline = buildStepPipeline(_account, _stepData.photo.value);
    this.persist();
    this.emitChange();
  },

  clearSavedProgress() {
    localStorage.removeItem(localStorageKeyForScenario(_account.registrationScenario));
  },

  getAccount() {
    return _account;
  },
  getRegistrationScenario() {
    return _account.registrationScenario || REGISTRATION_SCENARIOS.SUB_ACCOUNT;
  },
  isSubAccountScenario() {
    return this.getRegistrationScenario() === REGISTRATION_SCENARIOS.SUB_ACCOUNT;
  },
  isNewUserScenario() {
    return this.getRegistrationScenario() === REGISTRATION_SCENARIOS.NEW_USER;
  },
  getClinicName() {
    if (this.isNewUserScenario() && !_account.clinicName) {
      return "Wizecare";
    }
    return _account.clinicName || "Wizecare";
  },
  getRole() {
    return normalizeClinicianRole(_account.role);
  },
  getEmail() {
    return _account.email || "";
  },
  getStepPipeline() {
    return _stepPipeline;
  },
  getCurrentStepId() {
    return _stepPipeline[_stepIndex];
  },
  getStepIndex() {
    return _stepIndex;
  },
  getTotalSteps() {
    return _stepPipeline.length;
  },
  isSuccessStep() {
    return _stepIndex >= _stepPipeline.length;
  },
  getStepData() {
    return _stepData;
  },
  updateField(field, value) {
    if (_stepData[field]) {
      _stepData[field].value = value;
    }
    this.validateCurrentStep();
    this.persist();
    this.emitChange();
  },
  updateFields(patch) {
    Object.entries(patch).forEach(([field, value]) => {
      if (_stepData[field]) {
        _stepData[field].value = value;
      }
    });
    this.validateCurrentStep();
    this.persist();
    this.emitChange();
  },

  validateStepById(stepId) {
    const d = _stepData;
    switch (stepId) {
      case WIZARD_STEP_IDS.WELCOME_INTRO:
        return true;
      case WIZARD_STEP_IDS.NAME:
        return trimRequired(d.firstName.value) && trimRequired(d.lastName.value);
      case WIZARD_STEP_IDS.SECURITY: {
        let valid = true;
        if (needsPasswordOnSecurityStep(_account)) {
          valid = isPasswordPolicyMet(d.password.value);
        }
        if (needsTermsOnSecurityStep(_account)) {
          valid = valid && d.acceptTerms.value === true;
        }
        return valid;
      }
      case WIZARD_STEP_IDS.CLINICAL:
        return isValidProfession(d.profession.value);
      case WIZARD_STEP_IDS.CLINIC:
        // Patient language is a default preference (ipapi-backed), not a mandated setting.
        return true;
      case WIZARD_STEP_IDS.PHOTO: {
        if (isProfilePhoneEmpty(d.phone.value)) return true;
        return !getProfilePhoneError(d.phone.value);
      }
      case WIZARD_STEP_IDS.ABOUT:
        return (
          trimRequired(d.gender.value) &&
          isOptionalDobValid(d.birthMonth.value, d.birthDay.value, d.birthYear.value)
        );
      default:
        return true;
    }
  },

  validateCurrentStep() {
    return this.validateStepById(this.getCurrentStepId());
  },

  isNextDisabled() {
    const stepId = this.getCurrentStepId();
    if (stepId === WIZARD_STEP_IDS.CLINICAL) {
      return false;
    }
    if (stepId === WIZARD_STEP_IDS.WELCOME_INTRO) {
      return false;
    }
    return !this.validateStepById(stepId);
  },

  persist() {
    try {
      localStorage.setItem(
        localStorageKeyForScenario(_account.registrationScenario),
        JSON.stringify({
          expiry: Date.now() + 60 * 60 * 1000,
          stepIndex: _stepIndex,
          pipeline: _stepPipeline,
          data: _stepData,
        })
      );
    } catch (e) {
      // ignore
    }
  },

  nextStep() {
    if (_stepIndex < _stepPipeline.length) {
      _stepIndex++;
      // Second wizard step (after welcome). Starts ipapi here so the clinic step can preselect
      // Default Patients Language; the later phone field then reuses the cache.
      if (_stepIndex === 1) {
        this.applyGeoLanguageDefaults();
      }
      this.persist();
      this.emitChange();
    }
  },

  previousStep() {
    if (_stepIndex > 0) {
      _stepIndex--;
      this.persist();
      this.emitChange();
    }
  },

  goToSuccess() {
    _stepIndex = _stepPipeline.length;
    localStorage.removeItem(localStorageKeyForScenario(_account.registrationScenario));
    this.emitChange();
  },

  getClinicalStepToastMessage() {
    return "Your account has been created!";
  },

  saveStep(stepId, { demo = false } = {}) {
    if (demo) {
      return Promise.resolve(true);
    }

    const d = _stepData;
    const accountId = _account.accountId;

    switch (stepId) {
      case WIZARD_STEP_IDS.WELCOME_INTRO:
        return Promise.resolve(true);
      case WIZARD_STEP_IDS.NAME:
        return saveWelcomeStep(accountId, {
          firstName: d.firstName.value.trim(),
          lastName: d.lastName.value.trim(),
          profilePhotoFile: d.photo.value?.file,
          namesOnFile: _account.hasNameOnFile || hasNameOnFile(_account),
        }).then(isV2SaveSuccess);
      case WIZARD_STEP_IDS.SECURITY:
        return saveSecurityStep(accountId, {
          password: d.password.value,
          termsAccepted: d.acceptTerms.value,
          needsPassword: needsPasswordOnSecurityStep(_account),
          needsTerms: needsTermsOnSecurityStep(_account),
        }).then(isV2SaveSuccess);
      case WIZARD_STEP_IDS.CLINICAL:
        return saveClinicalStep(accountId, {
          profession: d.profession.value,
          experienceSince: d.experienceSince.value,
        }).then(isV2SaveSuccess);
      case WIZARD_STEP_IDS.CLINIC:
        return saveClinicSettingsStep(accountId, {
          languageForPatients: d.languageForPatients.value,
        }).then(isV2SaveSuccess);
      case WIZARD_STEP_IDS.PHOTO:
        return savePhotoPhoneStep(accountId, {
          email: _account.email,
          phone: d.phone.value,
          phoneEdited: d.phoneEdited.value,
        });
      case WIZARD_STEP_IDS.ABOUT:
        return saveAboutYouStep(accountId, {
          gender: d.gender.value,
          birthMonth: d.birthMonth.value,
          birthDay: d.birthDay.value,
          birthYear: d.birthYear.value,
          clinicianLanguage: d.clinicianLanguage.value,
        }).then(isV2SaveSuccess);
      default:
        return Promise.resolve(true);
    }
  },
});

export default RegistrationWizardState;
